import { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useClusterMetrics } from '../hooks/useClusterMetrics';

const MonitoringContext = createContext();

// Constants for polling intervals
const REALTIME_POLL_INTERVAL = 15000; // 15 seconds for real-time data
const METRICS_REFRESH_INTERVAL = 60000; // 60 seconds for historical metrics refresh

export function MonitoringProvider({ children, clusterId, connectionMode, timeRange }) {
  const [realTimeData, setRealTimeData] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [latestMetric, setLatestMetric] = useState(null);
  const [finops, setFinops] = useState(null);  // ClickHouse-backed FinOps monitoring (null when CH off)
  const [shadow, setShadow] = useState(null);        // shadow what-if savings timeline ({timeline, summary})
  const [projection, setProjection] = useState(null); // EOM cost projection (forecast half of Budgets)
  const [budget, setBudget] = useState(null);        // per-cluster budget (Phase 2; null/404 until set)
  const [costData, setCostData] = useState({
    podCosts: [],
    costProjection: null,
    nodeHistory: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const realtimeIntervalRef = useRef(null);
  const metricsIntervalRef = useRef(null);
  const isMountedRef = useRef(true);
  // Store last valid data to prevent zeroing out on temporary failures
  const lastValidDataRef = useRef({ realTime: null, latestMetric: null });

  // Helper to check if data is valid (has actual cluster data, not just empty response)
  const isValidSnapshot = useCallback((data) => {
    if (!data) return false;
    // Valid if we have pods OR nodes OR cpu/memory usage data
    // Note: cost_per_hour can be 0 for local clusters (minikube), so don't check it
    return (
      (data.pods_count > 0) ||
      (data.nodes_count > 0) ||
      (data.cpu_usage > 0) ||
      (data.memory_usage > 0)
    );
  }, []);

  // Fetch historical metrics for charts
  const fetchMetrics = useCallback(async () => {
    if (!clusterId) return;

    try {
      // Fetch core data first so the UI can render quickly
      const metricsEndpoint = connectionMode === 'agent'
        ? `${API}/agent-metrics/${clusterId}`
        : `${API}/metrics/${clusterId}`;

      const params = { limit: 200 };
      if (timeRange) params.hours = timeRange;

      let metricsRes = await axios.get(metricsEndpoint, { params }).catch(() => ({ data: [] }));
      if ((!metricsRes.data || metricsRes.data.length === 0) && connectionMode !== 'agent') {
        // Fallback to agent metrics if standard metrics are empty
        metricsRes = await axios.get(`${API}/agent-metrics/${clusterId}`, { params }).catch(() => ({ data: [] }));
      }

      const normalizeCostEstimate = (metric) => {
        // Prefer accurate_cost (Infracost) over cost_estimate (hardcoded)
        const ac = metric?.accurate_cost ?? metric?.metrics?.accurate_cost;
        if (ac && typeof ac === 'object' && ac.daily_cost != null) {
          return Number(ac.daily_cost);
        }
        const raw = metric?.cost_estimate ?? metric?.metrics?.cost_estimate;
        if (typeof raw === 'number') {
          return raw;
        }
        if (raw && typeof raw === 'object') {
          const daily = raw.daily_cost ?? raw.total_daily_cost ?? raw.cost ?? raw.value;
          if (typeof daily === 'number') {
            return daily;
          }
        }
        return 0;
      };

      // Process metrics and normalize cost_estimate to a number for charts
      const normalizedMetrics = (metricsRes.data || []).map((m) => ({
        ...m,
        cost_estimate: normalizeCostEstimate(m)
      }));

      const sortedMetrics = normalizedMetrics.sort((a, b) =>
        new Date(a.timestamp) - new Date(b.timestamp)
      );

      if (isMountedRef.current && sortedMetrics.length > 0) {
        setMetrics(sortedMetrics);
        const latest = sortedMetrics[sortedMetrics.length - 1];
        setLatestMetric(latest);
        lastValidDataRef.current.latestMetric = latest;
      }

      return sortedMetrics;
    } catch (err) {
      console.error('Failed to fetch metrics:', err);
      return [];
    }
  }, [clusterId, connectionMode, timeRange]);

  // Fetch real-time data
  const fetchRealTimeData = useCallback(async () => {
    if (!clusterId) return;

    try {
      const response = await axios.get(`${API}/realtime-metrics/${clusterId}`);
      const nextData = response.data;

      // Only update if we have valid data
      if (isValidSnapshot(nextData)) {
        if (isMountedRef.current) {
          setRealTimeData(nextData);
          lastValidDataRef.current.realTime = nextData;
          
          // Also update latestMetric to keep consistency
          setLatestMetric(prev => {
            const updated = {
              ...prev,
              cpu_usage: nextData.cpu_usage,
              memory_usage: nextData.memory_usage,
              cost_estimate: nextData.cost_per_hour * 24, // Convert hourly to daily
              pods_count: nextData.pods_count,
              nodes_count: nextData.nodes_count
            };
            lastValidDataRef.current.latestMetric = updated;
            return updated;
          });
        }
      } else {
        // If invalid data received, keep the last valid data
        console.warn('Received invalid realtime data, keeping previous values');
      }
    } catch (err) {
      console.error('Failed to fetch real-time metrics:', err);
      // On error, don't clear existing data - keep showing last valid values
    }
  }, [clusterId, isValidSnapshot]);

  // Fetch all monitoring data (initial load)
  // Fetch FinOps monitoring (ClickHouse-backed). Degrades silently to null on
  // 503 (ClickHouse not configured) so the FinOps tab shows an enable-hint
  // instead of erroring the whole page.
  const fetchFinops = useCallback(async () => {
    if (!clusterId) return null;
    try {
      const res = await axios.get(`${API}/finops/monitoring/${clusterId}`, {
        params: { hours: timeRange || 24 },
      });
      if (isMountedRef.current) setFinops(res.data);
      return res.data;
    } catch (err) {
      if (isMountedRef.current) setFinops(null);
      return null;
    }
  }, [clusterId, timeRange]);

  // Surface the existing cost endpoints into Monitoring with the same
  // graceful-degradation contract as fetchFinops (any failure → null, never
  // throws, never breaks the page). Each carries a hard timeout and is fired
  // fire-and-forget (NOT awaited in the gating load) so a slow endpoint can
  // never freeze the page. Fetched on mount + time-range change only, never on
  // the 15s realtime poll. The heavy allocation engine is owned by the Cost
  // Allocation panel itself (with its own loading state), not the context.
  const fetchShadow = useCallback(async () => {
    if (!clusterId) return null;
    try {
      const res = await axios.get(`${API}/clusters/${clusterId}/shadow-cost`, { params: { days: 30 }, timeout: 15000 });
      if (isMountedRef.current) setShadow(res.data);
      return res.data;
    } catch (_e) { if (isMountedRef.current) setShadow(null); return null; }
  }, [clusterId]);

  const fetchProjection = useCallback(async () => {
    if (!clusterId) return null;
    try {
      const res = await axios.get(`${API}/cost-projection/${clusterId}`, { timeout: 15000 });
      if (isMountedRef.current) setProjection(res.data);
      return res.data;
    } catch (_e) { if (isMountedRef.current) setProjection(null); return null; }
  }, [clusterId]);

  const fetchBudget = useCallback(async () => {
    if (!clusterId) return null;
    try {
      const res = await axios.get(`${API}/v1/clusters/${clusterId}/budget`, { timeout: 15000 });
      if (isMountedRef.current) setBudget(res.data);
      return res.data;
    } catch (_e) { if (isMountedRef.current) setBudget(null); return null; }
  }, [clusterId]);

  const fetchAllData = useCallback(async () => {
    if (!clusterId) return;

    try {
      setLoading(true);
      setError(null);

      // Core load gates `loading`: metrics, realtime, and (fast, CH-backed) FinOps.
      const [metricsData, realTimeRes] = await Promise.all([
        fetchMetrics(),
        axios.get(`${API}/realtime-metrics/${clusterId}`).catch(() => ({ data: null })),
        fetchFinops(),
      ]);

      // Secondary cost-report surfaces — fire-and-forget so they never block
      // first render; each is timed-out + graceful and fills in when ready.
      fetchShadow();
      fetchProjection();
      fetchBudget();

      if (isMountedRef.current) {
        // Set real-time data if valid
        if (isValidSnapshot(realTimeRes.data)) {
          setRealTimeData(realTimeRes.data);
          lastValidDataRef.current.realTime = realTimeRes.data;
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err.response?.data?.detail || 'Failed to fetch monitoring data');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [clusterId, fetchMetrics, fetchFinops, fetchShadow, fetchProjection, fetchBudget, isValidSnapshot]);

  // Initial fetch and setup polling
  useEffect(() => {
    isMountedRef.current = true;
    
    // Initial data fetch
    fetchAllData();

    // Poll real-time data frequently
    realtimeIntervalRef.current = setInterval(fetchRealTimeData, REALTIME_POLL_INTERVAL);
    
    // Refresh historical metrics less frequently (for chart updates)
    metricsIntervalRef.current = setInterval(fetchMetrics, METRICS_REFRESH_INTERVAL);

    return () => {
      isMountedRef.current = false;
      if (realtimeIntervalRef.current) {
        clearInterval(realtimeIntervalRef.current);
      }
      if (metricsIntervalRef.current) {
        clearInterval(metricsIntervalRef.current);
      }
    };
  }, [clusterId, fetchAllData, fetchRealTimeData, fetchMetrics]);

  // Use realtime data if valid, otherwise fall back to last valid or latestMetric
  const effectiveRealtime = isValidSnapshot(realTimeData) 
    ? realTimeData 
    : lastValidDataRef.current.realTime;
  
  const effectiveLatestMetric = latestMetric || lastValidDataRef.current.latestMetric;

  // Authoritative live + MTD numbers from the shared hook. Every page (Dashboard,
  // Monitoring header, Cost Breakdown tab, etc.) consumes these so the same
  // metric never shows two different values. The local realTimeData fallback
  // above is retained only for fields the hook doesn't yet expose (e.g.
  // efficiency_metrics breakdown by resource).
  const shared = useClusterMetrics(clusterId);
  const computedData = {
    // Live snapshot — single source of truth across pages.
    costPerHour: shared.costPerHour,
    costPerMinute: shared.costPerMinute,
    costPerSecond: shared.costPerSecond,
    dailyCost: shared.dailyCost,
    // Two distinct things, both useful — surface BOTH so consumers can label clearly:
    //   monthlyProjection = current hourly × 24 × 30  (live-rate annualization)
    //   mtdSpend          = actual $ spent month-to-date (shadow-engine integral)
    monthlyProjection: shared.monthlyProjection,
    mtdSpend: shared.mtdSpend,
    mtdSavings: shared.mtdSavings,
    mtdSavingsPct: shared.mtdSavingsPct,
    // Resource counts + usage
    podsCount: shared.podsCount || effectiveRealtime?.pods_count || effectiveLatestMetric?.pods_count || 0,
    nodesCount: shared.nodesCount || effectiveRealtime?.nodes_count || effectiveLatestMetric?.nodes_count || 0,
    cpuUsage: shared.cpuUsage,
    memoryUsage: shared.memoryUsage,
    // Efficiency = (cpu + memory) / 2 — utilization-based. Matches Dashboard
    // after the Dashboard Efficiency Score migration to this same source.
    efficiency: {
      cpu_efficiency: shared.cpuUsage,
      memory_efficiency: shared.memoryUsage,
      overall_efficiency: shared.efficiency,
    },
    overallEfficiency: shared.efficiency,
    // Deprecated alias — still emitted so legacy consumers compile, but
    // points at the same number as monthlyProjection. New code should pick
    // monthlyProjection or mtdSpend explicitly.
    monthlyCost: shared.monthlyProjection,
  };

  const value = {
    // Raw data
    realTimeData: effectiveRealtime,
    metrics,
    latestMetric: effectiveLatestMetric,
    costData,
    finops,  // ClickHouse-backed FinOps monitoring (null when CH off)
    shadow,       // shadow what-if savings ({timeline, summary}) — Savings tab
    projection,   // EOM cost projection — Budgets forecast
    budget,       // per-cluster budget (null until set / Phase 2 endpoint live)

    // Computed consistent values
    ...computedData,

    // State
    loading,
    error,
    timeRange,

    // Actions
    refresh: fetchAllData,
    refreshRealTime: fetchRealTimeData,
    refreshMetrics: fetchMetrics,
    refreshFinops: fetchFinops,
    refreshShadow: fetchShadow,
    refreshProjection: fetchProjection,
    refreshBudget: fetchBudget
  };

  return (
    <MonitoringContext.Provider value={value}>
      {children}
    </MonitoringContext.Provider>
  );
}

export function useMonitoring() {
  const context = useContext(MonitoringContext);
  if (!context) {
    throw new Error('useMonitoring must be used within a MonitoringProvider');
  }
  return context;
}

export default MonitoringContext;
